import type React from 'react'
import type { ButtonSize, ButtonVariant } from './Button'
import styles from './ButtonGroup.module.css'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type ButtonGroupItem = {
  /** 一意な識別子 */
  value: string
  /** 表示ラベル */
  label: React.ReactNode
  /** 左側に表示するアイコン */
  icon?: React.ReactNode
  /** このアイテムのみ無効化する */
  disabled?: boolean
}

export type ButtonGroupProps = {
  /** ボタンアイテム一覧 */
  items: ButtonGroupItem[]
  /** 現在選択されている value */
  value?: string
  /** 選択が変わったときのコールバック */
  onChange?: (value: string) => void
  /** ボタンサイズ（デフォルト: 'md'） */
  size?: ButtonSize
  /** カラーバリアント（デフォルト: 'primary'） */
  variant?: ButtonVariant
  /** グループ全体を無効化する */
  disabled?: boolean
  /** 親要素の幅いっぱいに広げる */
  fullWidth?: boolean
  className?: string
}

// ─────────────────────────────────────────────
// バリアントごとのカラートークン
// ─────────────────────────────────────────────
type VariantTokens = {
  bgActive: string
  bgActiveHover: string
  bgActivePressed: string
  colorActive: string
  bgInactive: string
  bgInactiveHover: string
  colorInactive: string
  borderInactive: string
}

const VARIANT_TOKENS: Record<ButtonVariant, VariantTokens> = {
  primary: {
    bgActive: 'var(--mantine-color-primary-5)',
    bgActiveHover: 'var(--mantine-color-primary-6)',
    bgActivePressed: 'var(--mantine-color-primary-7)',
    colorActive: '#000000',
    bgInactive: '#FFFFFF',
    bgInactiveHover: 'var(--mantine-color-primary-0)',
    colorInactive: 'var(--mantine-color-primary-5)',
    borderInactive: 'var(--mantine-color-primary-5)',
  },
  primaryOutlined: {
    bgActive: '#FFFFFF',
    bgActiveHover: 'var(--mantine-color-primary-0)',
    bgActivePressed: 'var(--mantine-color-primary-1)',
    colorActive: 'var(--mantine-color-primary-5)',
    bgInactive: '#FFFFFF',
    bgInactiveHover: '#F5F5F5',
    colorInactive: '#8A8B8F',
    borderInactive: '#CBCBCE',
  },
  secondary: {
    bgActive: 'var(--mantine-color-secondary-5)',
    bgActiveHover: 'var(--mantine-color-secondary-6)',
    bgActivePressed: 'var(--mantine-color-secondary-7)',
    colorActive: '#000000',
    bgInactive: '#FFFFFF',
    bgInactiveHover: 'var(--mantine-color-secondary-0)',
    colorInactive: 'var(--mantine-color-secondary-5)',
    borderInactive: 'var(--mantine-color-secondary-5)',
  },
  tertiary: {
    bgActive: '#5BBF3A',
    bgActiveHover: '#4DA532',
    bgActivePressed: '#3F8C2A',
    colorActive: '#000000',
    bgInactive: '#FFFFFF',
    bgInactiveHover: '#F2FBF0',
    colorInactive: '#5BBF3A',
    borderInactive: '#5BBF3A',
  },
  system: {
    bgActive: '#757575',
    bgActiveHover: '#616161',
    bgActivePressed: '#424242',
    colorActive: '#FFFFFF',
    bgInactive: '#FFFFFF',
    bgInactiveHover: '#EEEEEE',
    colorInactive: '#757575',
    borderInactive: '#757575',
  },
  danger: {
    bgActive: '#D46A6A',
    bgActiveHover: '#C55757',
    bgActivePressed: '#AD4343',
    colorActive: '#FFFFFF',
    bgInactive: '#FFFFFF',
    bgInactiveHover: '#FBF1F1',
    colorInactive: '#D46A6A',
    borderInactive: '#D46A6A',
  },
}

// ─────────────────────────────────────────────
// サイズ設定（Button と同一体系）
// ─────────────────────────────────────────────
const SIZE_CONFIG: Record<ButtonSize, { height: number; fontSize: number; px: number }> = {
  lg: { height: 56, fontSize: 15, px: 20 },
  md: { height: 48, fontSize: 15, px: 20 },
  sm: { height: 36, fontSize: 13, px: 16 },
  xs: { height: 32, fontSize: 13, px: 12 },
}

// border-radius 値（round なし: 8px）
const RADIUS = 8

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
export const ButtonGroup = ({
  items,
  value,
  onChange,
  size = 'md',
  variant = 'primary',
  disabled = false,
  fullWidth = false,
  className,
}: ButtonGroupProps) => {
  const vt = VARIANT_TOKENS[variant]
  const sc = SIZE_CONFIG[size]

  const containerClass = [fullWidth ? styles.groupFullWidth : styles.group, className].filter(Boolean).join(' ')

  return (
    <div
      className={containerClass}
      style={
        {
          '--bg-radius': `${RADIUS}px`,
          '--bg-active': vt.bgActive,
          '--bg-active-hover': vt.bgActiveHover,
          '--bg-active-pressed': vt.bgActivePressed,
          '--color-active': vt.colorActive,
          '--bg-inactive': vt.bgInactive,
          '--bg-inactive-hover': vt.bgInactiveHover,
          '--color-inactive': vt.colorInactive,
          '--border-inactive': vt.borderInactive,
        } as React.CSSProperties
      }
      role="group"
    >
      {items.map((item, index) => {
        const isActive = item.value === value
        const isDisabled = disabled || item.disabled

        const posClass =
          items.length === 1
            ? styles['item--only']
            : index === 0
              ? styles['item--first']
              : index === items.length - 1
                ? styles['item--last']
                : styles['item--middle']

        const stateClass = isActive ? styles['item--active'] : styles['item--inactive']

        return (
          <button
            key={item.value}
            type="button"
            disabled={isDisabled}
            aria-pressed={isActive}
            onClick={() => {
              if (!isDisabled) onChange?.(item.value)
            }}
            className={[styles.item, posClass, stateClass].join(' ')}
            style={{
              height: `${sc.height}px`,
              fontSize: `${sc.fontSize}px`,
              paddingLeft: `${sc.px}px`,
              paddingRight: `${sc.px}px`,
            }}
          >
            {item.icon}
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
